import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { Question } from '../../types/questionnaire.types';

interface MultipleChoiceCheckboxQuestionProps {
  question: Question;
  response: string;
  onCheckboxChange: (
    questionId: string,
    answerId: string,
    checked: boolean
  ) => void;
}

export const MultipleChoiceCheckboxQuestion: React.FC<
  MultipleChoiceCheckboxQuestionProps
> = ({ question, response, onCheckboxChange }) => {
  if (!question.answers) return null;

  const selectedAnswers = response ? response.split(',') : [];
  const selectedCount = selectedAnswers.length;
  const hasMaxSelections =
    question.maxSelections !== undefined && question.maxSelections !== null;
  const maxSelections = question.maxSelections || 0;
  const remainingSelections = hasMaxSelections
    ? maxSelections - selectedCount
    : 0;
  const isMaxReached = hasMaxSelections && selectedCount >= maxSelections;

  return (
    <FormControl component="fieldset" sx={{ width: '100%' }}>
      {hasMaxSelections && (
        <Typography
          sx={{
            fontFamily: 'Roboto-Regular !important',
            fontSize: '10pt !important',
            color: remainingSelections === 0 ? '#10b981' : '#667eea',
            mb: 1.5,
            fontWeight: 500,
          }}
        >
          {remainingSelections > 0
            ? `Selecione ${remainingSelections} ${remainingSelections === 1 ? 'opção' : 'opções'} (${selectedCount}/${maxSelections})`
            : `✓ ${maxSelections} ${maxSelections === 1 ? 'opção selecionada' : 'opções selecionadas'}`}
        </Typography>
      )}
      <FormGroup>
        {question.answers.map((answer) => {
          const isChecked = selectedAnswers.includes(answer.id);
          const isDisabled = !isChecked && isMaxReached;

          return (
            <FormControlLabel
              key={answer.id}
              disabled={isDisabled}
              control={
                <Checkbox
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={(e) =>
                    onCheckboxChange(question.id, answer.id, e.target.checked)
                  }
                  icon={
                    <CheckBoxOutlineBlankIcon
                      sx={{
                        color: isDisabled ? '#e5e7eb' : '#d1d5db',
                        fontSize: '28px',
                        transition: 'all 0.2s ease',
                      }}
                    />
                  }
                  checkedIcon={
                    <CheckBoxIcon
                      sx={{
                        color: '#10b981',
                        fontSize: '28px',
                        filter:
                          'drop-shadow(0 2px 4px rgba(16, 185, 129, 0.3))',
                        transition: 'all 0.2s ease',
                      }}
                    />
                  }
                  sx={{
                    padding: '8px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isDisabled
                        ? 'transparent'
                        : 'rgba(16, 185, 129, 0.08)',
                      transform: isDisabled ? 'none' : 'scale(1.1)',
                    },
                    '&:hover .MuiSvgIcon-root': {
                      color: isDisabled ? '#e5e7eb' : '#10b981',
                    },
                    '&.Mui-checked': {
                      animation: 'pulse 0.3s ease-in-out',
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: 'Roboto-Regular !important',
                    fontSize: '11pt !important',
                    color: isDisabled ? '#9ca3af' : '#4b5563',
                  }}
                >
                  {answer.text}
                </Typography>
              }
              sx={{
                m: 0,
                mb: 1.5,
                borderRadius: '12px',
                border: '1px solid #F0F0F0',
                borderColor: isChecked
                  ? '#667eea'
                  : isDisabled
                    ? '#f3f4f6'
                    : '#e5e7eb',
                background: isChecked
                  ? 'rgba(102, 126, 234, 0.05)'
                  : isDisabled
                    ? '#f9fafb'
                    : '#ffffff',
                transition: 'all 0.2s ease',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  borderColor: isDisabled ? '#f3f4f6' : '#667eea',
                  background: isDisabled
                    ? '#f9fafb'
                    : 'rgba(102, 126, 234, 0.03)',
                  transform: isDisabled ? 'none' : 'translateX(4px)',
                },
              }}
            />
          );
        })}
      </FormGroup>
    </FormControl>
  );
};
